import styled, { css, DefaultTheme } from 'styled-components';

import { useLinger } from 'hooks/useLinger';

type FoldoutPosition = 'left' | 'right';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean;
  position?: FoldoutPosition;
  closeOnBackdropClick?: boolean;
  setOpen?: (open: boolean) => void;
  width?: string;
  onBackdropClick?: () => void;
  isNested?: boolean;
}

export default function Foldout({
  children,
  open,
  position = 'right',
  closeOnBackdropClick = false,
  setOpen,
  width = 'min(80%, 360px)',
  onBackdropClick,
  isNested = false,
  ...rest
}: Props) {
  const linger = useLinger(open, 500);

  return (
    <>
      <Backdrop
        $open={open}
        $isNested={isNested}
        onClick={() => {
          if (closeOnBackdropClick || onBackdropClick) {
            onBackdropClick?.();
            setOpen?.(false);
          }
        }}
      />

      <Slideout $open={open} $position={position} $width={width} $isNested={isNested} {...rest}>
        {linger && children}
      </Slideout>
    </>
  );
}

export const Backdrop = styled.div<{ $open: boolean; $isNested: boolean }>`
  z-index: ${({ $isNested }) => ($isNested ? '1000' : '800')};
  position: fixed;
  pointer-events: ${({ $open }) => ($open ? 'initial' : 'none')};
  background-color: ${({ $open }) => ($open ? 'var(--black-50)' : 'transparent')};
  transition: background-color 0.5s;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
`;

const positionStyles = (theme: DefaultTheme, position: FoldoutPosition, $open: boolean) =>
  ({
    left: css`
      left: 0;
      transform: translateX(-${$open ? '0' : '100'}%);
    `,
    right: css`
      right: 0;
      transform: translateX(${$open ? '0' : '100'}%);
    `,
  })[position];

const Slideout = styled.div<{ $open: boolean; $position: FoldoutPosition; $width: string; $isNested: boolean }>`
  z-index: ${({ $isNested }) => ($isNested ? '1100' : '900')};
  position: fixed;
  top: 0;
  bottom: 0;
  background-color: white;
  transition: transform 0.5s;
  width: ${(props) => props.$width};
  overflow-y: auto;

  ${({ theme, $position, $open }) => positionStyles(theme, $position, $open)}
`;
